import { Button, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import SaveIcon from '@mui/icons-material/Save';
import { useCallback, useEffect, useState } from "react";
import CampoCadastroItem from "./CampoCadastroItem";
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast, ToastContainer } from "react-toastify";
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import { CadastroModelo, CampoCadastro, TIPOS_IDENTIFICADOR } from "../../../interface/ICadastroModelo";

const defaultCampo: CampoCadastro = { nome: 'Nome do Campo', tipo: 'text', obrigatorio: false, identificador: false, opcoes: [] };

export default function EditarCadastroModelo({ cadastroModelos, companyId, id }: { cadastroModelos: any; companyId: string; id: string | undefined }) {
  const navigate = useNavigate();
  const location = useLocation();
  const modeloState = location.state?.cadastroModelo as Partial<CadastroModelo> | undefined;

  const camposIniciais = modeloState?.campos;
  const formModelo = {
    nome: modeloState?.nome || 'Nome do Cadastro-Modelo',
    campos: Array.isArray(camposIniciais) ? camposIniciais : [defaultCampo],
  };

  const [nomeModelo, setNomeModelo] = useState<string>(formModelo.nome);
  const [campos, setCampos] = useState<CampoCadastro[]>(formModelo.campos);

  useEffect(() => {
    const getModelo = async () => {
      if (!companyId || !id) return;
      try {
        const response = await cadastroModelos.getCadastroModelo();
        if (response) {
          setNomeModelo(response.nome || 'Nome do Cadastro-Modelo');
          setCampos(Array.isArray(response.campos) ? response.campos : [defaultCampo]);
        }
      } catch (error) { }
    };

    if (!modeloState && id !== 'novo') getModelo();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, id, modeloState]);

  const identificadores = campos.filter(c => c.identificador).length;
  const hasExactlyOneIdentificador = identificadores === 1;

  const addCampo = useCallback(() => {
    setCampos(prev => [...prev, { ...defaultCampo }]);
  }, []);

  const updateCampo = useCallback((index: number, campo: Partial<CampoCadastro>) => {
    setCampos(prev => prev.map((c, i) => i === index ? { ...prev[index], ...campo } : c));
  }, []);

  const deleteCampo = useCallback((index: number) => {
    setCampos(prev => prev.filter((_, i) => i !== index));
  }, []);

  const duplicateCampo = useCallback((index: number) => {
    setCampos(prev => {
      const newCampo = { ...prev[index], nome: prev[index].nome + ' (cópia)', identificador: false };
      const newCampos = [...prev];
      newCampos.splice(index + 1, 0, newCampo);
      return newCampos;
    });
  }, [])

  const moveCampo = useCallback((index: number, direction: 'up' | 'down') => {
    setCampos(prev => {
      if ((direction === 'up' && index === 0) || (direction === 'down' && index === prev.length - 1)) return prev;
      const newCampos = [...prev];
      const target = newCampos[index + (direction === 'up' ? -1 : 1)];
      newCampos[index + (direction === 'up' ? -1 : 1)] = newCampos[index];
      newCampos[index] = target;
      return newCampos;
    });
  }, [])

  const handleSave = useCallback(() => {
    if (!hasExactlyOneIdentificador) {
      toast.info('É necessário exatamente um campo identificador para salvar', { toastId: 'save_warning' });
      return;
    }
    if (id === 'novo') {
      cadastroModelos.createCadastroModelo({ nomeModelo, campos });
    } else {
      cadastroModelos.updateCadastroModelo({ nomeModelo, campos });
    }
  }, [cadastroModelos, campos, hasExactlyOneIdentificador, id, nomeModelo]);

  const hasDifferences = JSON.stringify(cadastroModelos.cadastroModeloData?.nome || formModelo.nome) !== JSON.stringify(nomeModelo) ||
    JSON.stringify(cadastroModelos.cadastroModeloData?.campos || formModelo.campos) !== JSON.stringify(campos);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        if (!hasExactlyOneIdentificador) {
          toast.info("É necessário exatamente um campo identificador para salvar", { toastId: 'save_warning' });
          return;
        }
        if (!hasDifferences) {
          toast.info("Nenhuma alteração para salvar", { toastId: 'save_info' });
          return;
        }
        handleSave();
      }
      if ((event.key === 'Escape')) {
        navigate(`/cadastro-modelo/${companyId}`);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    }
  }, [companyId, hasDifferences, hasExactlyOneIdentificador, handleSave, navigate]);

  const disableIdentificador = (campo: CampoCadastro) => {
    if (!TIPOS_IDENTIFICADOR.includes(campo.tipo)) return 'tipo';
    if (identificadores >= 1 && !campo.identificador) return 'jaExiste';
    return false;
  };

  if (cadastroModelos.loadingCadastroModelo) {
    return <Typography variant="h6" textAlign='center' width='100%' color="textSecondary">Carregando...</Typography>
  }
  if (cadastroModelos.errorCadastroModelo) {
    return (
      <Stack alignItems='center' width='100%' spacing={2} paddingTop={3}>
        <AssignmentLateIcon sx={{ fontSize: 42 }} color="error" />
        <Typography variant="h6" fontWeight={600}>Não foi possível carregar o cadastro-modelo</Typography>
        <Stack direction='row' spacing={2}>
          <Button variant="text" color="inherit" startIcon={<ArrowBackIcon />} onClick={() => navigate(`/cadastro-modelo/${companyId}`)}>Voltar para lista</Button>
          <Button variant="text" color="inherit" onClick={() => cadastroModelos.getCadastroModelo()}>Tentar novamente</Button>
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack direction='column' alignItems='flex-start' justifyContent='flex-start' width='100%' paddingX={'10%'} spacing={3}>
      <Stack direction='row' justifyContent='space-between' width='100%'>
        <Stack direction='row' alignItems='center' spacing={1}>
          <IconButton size="large" onClick={() => navigate(-1)}><ArrowBackIcon sx={{ color: '#000000' }} /></IconButton>
          <Typography variant="h6" fontWeight={700} sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{nomeModelo}</Typography>
        </Stack>
        <Tooltip title={!hasExactlyOneIdentificador ? "É necessário exatamente um campo identificador para salvar" : "Atalho: Ctrl + S"} placement="top">
          <span>
            <Button variant="contained" color="primary" size="large" disabled={!hasExactlyOneIdentificador || !hasDifferences} loading={cadastroModelos.loadingUpdate || cadastroModelos.loadingCreate} startIcon={<SaveIcon />} onClick={handleSave}>Salvar</Button>
          </span>
        </Tooltip>
      </Stack>
      <Stack borderRadius={2} direction={'row'} width='100%' spacing={2}>
        <TextField label="Nome do modelo" value={nomeModelo} onChange={(e) => setNomeModelo(e.target.value)} fullWidth variant="outlined" sx={{ bgcolor: 'grey.50' }} />
      </Stack>
      <Stack direction='column' gap={2} width='100%'>
        {campos.map((campo, index) => (
          <CampoCadastroItem campo={campo}
            updateCampo={updateCampo}
            deleteCampo={deleteCampo}
            duplicateCampo={duplicateCampo}
            moveCampo={moveCampo}
            disableIdentificador={disableIdentificador(campo)}
            total={campos.length - 1}
            index={index} key={index} />
        ))}
      </Stack>
      <Button variant='text' size="large" fullWidth onClick={addCampo} startIcon={<AddCircleOutlineOutlinedIcon />}>Adicionar Campo</Button>
      <ToastContainer />
    </Stack>
  );
};
